import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { GrantDetailDto, GrantListItemDto, Paginated } from '@zoxo/shared';

import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';

/**
 * 관심 공고 켜기·끄기 (PUT/DELETE /grants/:id/bookmark). 낙관적으로 상세 캐시의
 * `isBookmarked` 를 먼저 뒤집고, 실패하면 되돌린다. 마이 페이지 목록은 무효화한다.
 */
export function useToggleBookmark(grantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (next: boolean) =>
      next ? api.put<{ bookmarked: boolean }>(`/grants/${grantId}/bookmark`, {}) : api.del<{ bookmarked: boolean }>(`/grants/${grantId}/bookmark`),
    onMutate: async (next) => {
      await queryClient.cancelQueries({ queryKey: ['grant', grantId] });
      const prev = queryClient.getQueryData<GrantDetailDto>(['grant', grantId]);
      if (prev) queryClient.setQueryData<GrantDetailDto>(['grant', grantId], { ...prev, isBookmarked: next });
      return { prev };
    },
    onError: (_e, _next, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['grant', grantId], ctx.prev);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['bookmarks'] });
    },
  });
}

/** 내 관심 공고 (GET /users/me/bookmarks). 공고 목록과 같은 커서 무한 스크롤. 로그인했을 때만 부른다. */
export function useBookmarks() {
  const { isAuthenticated } = useAuth();
  return useInfiniteQuery({
    queryKey: ['bookmarks'],
    queryFn: ({ pageParam }) =>
      api.get<Paginated<GrantListItemDto>>(`/users/me/bookmarks${pageParam ? `?cursor=${encodeURIComponent(pageParam)}` : ''}`),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.meta.nextCursor ?? undefined,
    enabled: isAuthenticated,
  });
}
